import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import config from 'configs/app';
import getValueWithUnit from 'lib/getValueWithUnit';
import { useI18n } from 'lib/i18n/context';
import { currencyUnits } from 'lib/units';
import Skeleton from 'ui/shared/chakra/Skeleton';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import TxEntity from 'ui/shared/entities/tx/TxEntity';
import TxStatus from 'ui/shared/statusTag/TxStatus';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';
import TxFee from 'ui/shared/tx/TxFee';
import TxAdditionalInfo from 'ui/txs/TxAdditionalInfo';
import TxType from 'ui/txs/TxType';

type Props = {
  tx: Transaction;
  isLoading?: boolean;
  isLast?: boolean;
};

const LatestTxsItem = ({ tx, isLoading, isLast }: Props) => {
  const dataTo = tx.to ? tx.to : tx.created_contract;
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const labelColor = useColorModeValue('#616161', '#999999');
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const mutedColor = useColorModeValue('#999999', '#616161');
  const itemBg = useColorModeValue('#FFFFFF', '#111111');
  const { t } = useI18n();

  return (
    <Box
      width="100%"
      minH="138px"
      px="16px"
      py="16px"
      bg={ itemBg }
      borderBottom={ isLast ? 'none' : '1px solid' }
      borderColor={ borderColor }
    >
      <Flex alignItems="center" gap="8px" mb="16px">
        <TxType types={ tx.transaction_types } isLoading={ isLoading }/>
        <TxStatus status={ tx.status } errorText={ tx.status === 'error' ? tx.result : undefined } isLoading={ isLoading }/>
      </Flex>

      <Flex justifyContent="space-between" gap="12px" alignItems="flex-start">
        <Flex direction="column" gap="8px" w="224px" minW="224px">
          <Flex alignItems="center" gap="4px" minW={ 0 }>
            <TxAdditionalInfo tx={ tx } isLoading={ isLoading }/>
            <Box minW={ 0 }>
              <TxEntity
                isLoading={ isLoading }
                hash={ tx.hash }
                fontSize="14px"
                lineHeight="20px"
                fontWeight={ 500 }
                fontFamily="'Oxanium', var(--chakra-fonts-body)"
                color={ valueColor }
                _hover={{ color: valueColor, textDecoration: 'none' }}
              />
            </Box>
          </Flex>
          <TimeAgoWithTooltip
            timestamp={ tx.timestamp }
            enableIncrement
            isLoading={ isLoading }
            color={ mutedColor }
            fontWeight={ 500 }
            fontSize="14px"
            lineHeight="17.86px"
            fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
          />
        </Flex>

        <Flex direction="column" gap="8px" w="224px" minW="224px">
          <AddressEntity
            label={ t('common.from') }
            address={ tx.from }
            isLoading={ isLoading }
            noCopy
            truncation="constant_tokens"
            fontSize="14px"
            lineHeight="20px"
            fontWeight={ 500 }
            fontFamily="'Oxanium', var(--chakra-fonts-body)"
            sx={{
              a: {
                color: valueColor,
                _hover: {
                  color: valueColor,
                  textDecoration: 'none',
                },
              },
              button: {
                color: '#14DCAC',
              },
              '.chakra-text': {
                color: labelColor,
                fontFamily: '\'Space Grotesk\', var(--chakra-fonts-body)',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
              },
            }}
          />
          { dataTo ? (
            <AddressEntity
              label={ t('common.to') }
              address={ dataTo }
              isLoading={ isLoading }
              noCopy
              truncation="constant_tokens"
              fontSize="14px"
              lineHeight="20px"
              fontWeight={ 500 }
              fontFamily="'Oxanium', var(--chakra-fonts-body)"
              sx={{
                a: {
                  color: valueColor,
                  _hover: {
                    color: valueColor,
                    textDecoration: 'none',
                  },
                },
                button: {
                  color: '#14DCAC',
                },
                '.chakra-text': {
                  color: labelColor,
                  fontFamily: '\'Space Grotesk\', var(--chakra-fonts-body)',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                },
              }}
            />
          ) : null }
        </Flex>

        <Flex direction="column" alignItems="flex-end" gap="8px" w="144px" minW="144px">
          { !config.UI.views.tx.hiddenFields?.value ? (
            <Skeleton isLoaded={ !isLoading }>
              <Flex alignItems="center" gap="4px">
                <Text
                  color={ labelColor }
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight={ 500 }
                  fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
                >
                  { t('common.value') }
                </Text>
                <Text
                  color={ valueColor }
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight={ 500 }
                  fontFamily="'Oxanium', var(--chakra-fonts-body)"
                >
                  { getValueWithUnit(tx.value).dp(5).toFormat() } { currencyUnits.ether }
                </Text>
              </Flex>
            </Skeleton>
          ) : null }

          { !config.UI.views.tx.hiddenFields?.tx_fee ? (
            <Skeleton isLoaded={ !isLoading }>
              <Flex alignItems="center" gap="4px">
                <Text
                  color={ labelColor }
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight={ 500 }
                  fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
                >
                  { t('common.fee') }
                </Text>
                <TxFee
                  tx={ tx }
                  accuracy={ 5 }
                  color={ valueColor }
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight={ 500 }
                  fontFamily="'Oxanium', var(--chakra-fonts-body)"
                />
              </Flex>
            </Skeleton>
          ) : null }
        </Flex>
      </Flex>
    </Box>
  );
};

export default React.memo(LatestTxsItem);
